import {Box} from '@mui/material';
import InvestigateHeader from './header/InvestigateHeader';
import TranscriptPanel from './transcript-panel/TranscriptPanel';
import ArrSplitPanel from '../../common/arr-split-panel/ArrSplitPanel';
import './ViewAIInvestigate.scss';
import InvestigateChatPanel from './chat-interface/InvestigateChatPanel';
import TranscriptActionModal from './transcipts/transcript-actions-modal/TranscriptActionsModal';
import {
  MyTranscriptCreateKey,
  TranscriptFormValues,
  TranscriptItemDataType,
} from './AiInvestigateUtils';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../shared/constants';
import {useInvestigate} from '../../../hooks/ai/investigate/useInvestigate';
import {useEffect, useMemo, useState} from 'react';
import {getAIInvestigateIndicesList} from '../../../api/endpoints/aiInvestigateApi';
import {
  IAIInvestigateIndicesListItem,
  IAIInvestigateIndicesPayload,
  ICreateConversationResponse,
} from '../../../api/types/aiInvestigate.type';
import {ISelectItem} from '../../../shared/types';

const modalMode = MyTranscriptCreateKey.Create;
export const ViewAIInvestigate = () => {
  const {
    isCollapsed,
    setIsCollapsed,
    selectedTranscriptDetails,
    setSelectedTranscriptDetails,
    disableNewChat,
    setDisableNewChat,
    isModalOpen,
    setIsModalOpen,
    showToast,
    setShowToast,
    toastMessage,
    handleTranscriptSelect,
    hanleCreateNewTranscript,
    handleCreateTranscript,
  } = useInvestigate();

  const [activeIndexData, setActiveIndexData] = useState<ISelectItem[]>([]);
  const [newTranscript, setNewTranscript] =
    useState<TranscriptItemDataType | null>(null);
  const [updatedConversation, setUpdatedConversation] =
    useState<ICreateConversationResponse | null>(null);
  const hasActiveIndexes = activeIndexData.length > 0;
  const indicesPayload: IAIInvestigateIndicesPayload = useMemo(
    () => ({
      sortingColumn: 'LastModifiedDate',
      direction: 'DESC',
      displayLength: -1,
      pageNumber: 0,
      isActiveOnly: true,
      columnFilter: [],
    }),
    []
  );

  useEffect(() => {
    let isCancelled = false;

    const fetchActiveIndexes = async () => {
      try {
        const response = await getAIInvestigateIndicesList(indicesPayload);
        if (isCancelled) return;

        const mapped: ISelectItem[] = (response?.indices ?? []).map(
          (i: IAIInvestigateIndicesListItem) => ({
            id: i.indexId,
            name: i.indexName,
          })
        );

        setActiveIndexData(mapped);
      } catch {
        if (isCancelled) return;
        setActiveIndexData([]);
      }
    };

    fetchActiveIndexes();

    return () => {
      isCancelled = true;
    };
  }, [indicesPayload]);

  const onCreateTranscript: (data?: TranscriptFormValues) => void = (data) => {
    if (!data) return null;
    handleCreateTranscript(data).then((createdTranscript) => {
      if (!createdTranscript) return;
      setNewTranscript(createdTranscript);
    });
  };

  const handleTranscriptDeleted = (id: number) => {
    if (selectedTranscriptDetails.transcriptId === id) {
      // If the deleted transcript is currently selected, reset the selection
      setNewTranscript(null);
      setSelectedTranscriptDetails({
        transcriptId: 0,
        isOwner: true,
        ownerName: '',
        transcriptName: '',
        indexName: '',
        indexId: 0,
        isNew: false,
        documentCount: 0,
      });
    }
  };

  const conversationUpdateHandler = (response: ICreateConversationResponse) => {
    setUpdatedConversation(response);
  };

  const leftPanel = () => (
    <Box className="investigate-left-content">
      <TranscriptPanel
        setIsCollapsed={setIsCollapsed}
        onDisableNewChatChange={setDisableNewChat}
        selectedTranscriptId={selectedTranscriptDetails.transcriptId}
        onTranscriptSelect={handleTranscriptSelect}
        onNewChatClick={hanleCreateNewTranscript}
        hasActiveIndexes={hasActiveIndexes}
        newTranscript={newTranscript}
        onTranscriptDeleted={handleTranscriptDeleted}
        updatedConversation={updatedConversation}
      />
    </Box>
  );

  const rightPanel = () => (
    <InvestigateChatPanel
      transcriptDetails={selectedTranscriptDetails}
      isTranscriptPanelCollapsed={isCollapsed}
      setIsTranscriptPanelCollapsed={setIsCollapsed}
      setSelectedTranscriptDetails={setSelectedTranscriptDetails}
      disableNewChat={disableNewChat}
      onNewChatClick={hanleCreateNewTranscript}
      onConversationUpdate={conversationUpdateHandler}
    />
  );

  return (
    <Box
      className="view-ai-investigate-container"
      data-testid="view-ai-investigate"
    >
      <Box
        className="investigate-header"
        data-testid="view-ai-investigate-header"
      >
        <InvestigateHeader />
      </Box>
      <Box
        className={`investigate-content ${isCollapsed ? 'panel-collapsed' : 'panel-expanded'}`}
        data-testid="view-ai-investigate-content"
      >
        <ArrSplitPanel
          leftPanel={leftPanel()}
          rightPanel={rightPanel()}
          maxLeftPanelRatio={0.5}
        />
      </Box>
      {isModalOpen && (
        <TranscriptActionModal
          open={true}
          onClose={() => setIsModalOpen(false)}
          onConfirm={onCreateTranscript}
          mode={modalMode}
          indexData={activeIndexData}
        />
      )}

      {showToast && (
        <ArrToastNotification
          message={toastMessage?.error ?? toastMessage?.success ?? ''}
          status={toastMessage?.error ? [ToastType.Error] : [ToastType.Success]}
          onClose={() => setShowToast(false)}
          data-testid="toast-message"
          title="Error"
          setErrorIconAsAlert
        />
      )}
    </Box>
  );
};
